/*
  Copyright (c) Epic Games, Inc. All Rights Reserved.
  CONTAINS AI GENERATED CODE.
*/

import { BackgroundContainer, CosmosAsset, ResponsiveContainer, SectionContainer } from "@components/elements/atoms";
import { CosmosTabs as Tabs, useTabActiveIndex, SectionHeading } from "@components/elements/molecules";
import { css, cx } from "@panda/css";
import { TabbedBlogCardsItemFragment, TabbedBlogCardsSectionFragment } from "@cosmos/hygraph-schema";
import { useMemo } from "react";
import { Box, VStack } from "@panda/jsx";
import { TabbedBlogCardsMolecule } from "./TabbedBlogCards.molecule";

const analyticsEventValue = {
	component: "TabbedBlogCards",
	section: "section",
};
const bgCircularGradient = `linear-gradient(270deg, rgba(16, 16, 20, 0) 70%, #101014 100%), linear-gradient(180deg, #101014 0%, rgba(16, 16, 20, 0) 75%), linear-gradient(90deg, rgba(16, 16, 20, 0) 50%, #101014 100%), linear-gradient(180deg, rgba(16, 16, 20, 0) 50%, #101014 100%)`;

const bgOverlayStyles = css({
	position: "absolute",
	inset: "0",
	display: "flex",
	"& img, & video": {
		height: "100%",
		width: "100%",
		objectFit: "cover",
	},
	"&::after": {
		content: "''",
		display: "block",
		background: bgCircularGradient,
		position: "absolute",
		inset: 0,
		zIndex: 1,
	},
	zIndex: 0,
	"& .cosmos-attribution": {
		zIndex: 3,
	},
});

const tabTriggerStyle = css({
	paddingInline: "eds.dimension.16",
});

const sectionHeadingStyles = css({
	"& .title-desc-stack": {
		rowGap: "eds.dimension.16",
	},
});

export const TabbedBlogCardsSection = (props: TabbedBlogCardsSectionFragment): React.JSX.Element | null => {
	const tabs = useMemo(() => props?.tabs || [], [props?.tabs]);
	const [activeIndex, setActiveIndex] = useTabActiveIndex(tabs.length);

	// Check if ANY tab has valid content
	const hasValidContent = useMemo(() => {
		return tabs.some((tab) =>
			Boolean(tab?.title || tab?.description || tab?.cards?.length || tab?.featuredCards?.length),
		);
	}, [tabs]);

	// Get active tab for background asset (still needed outside CosmosTabsContent)
	const activeTab = useMemo(() => tabs[activeIndex] || tabs[0] || {}, [tabs, activeIndex]);

	if (!hasValidContent) return null;

	const enableTabs = tabs.length > 1;

	return (
		<BackgroundContainer>
			<ResponsiveContainer>
				<SectionContainer>
					{activeTab?.backgroundAsset ? (
						<Box className={cx("tabbed-blog-cards-bg-container", bgOverlayStyles)}>
							<CosmosAsset asset={activeTab?.backgroundAsset}></CosmosAsset>
						</Box>
					) : null}
					<Tabs
						activeIndex={activeIndex}
						onActiveIndexChange={(val) => val !== undefined && setActiveIndex(val)}
						size="lg"
					>
						<VStack
							gap="eds.dimension.32"
							alignItems="flex-start"
							className="tabbed-blog-cards-section"
							position="relative"
						>
							{enableTabs ? (
								<Tabs.ButtonList>
									{tabs.map((tab: TabbedBlogCardsItemFragment, index: number) => (
										<Tabs.Button
											key={tab.id || index}
											index={index}
											label={tab.tabLabel || ""}
											className={tabTriggerStyle}
											analytics={{
												eventCategory: "button",
												interactionType: "click",
												eventAction: "select",
												eventLabel: tab.tabLabel || "",
												eventValue: analyticsEventValue,
											}}
										/>
									))}
								</Tabs.ButtonList>
							) : null}

							{tabs.map((tab: TabbedBlogCardsItemFragment, index: number) => {
								const featuredCardsLength = tab?.featuredCards?.length ?? 0;
								const cardsLength = tab?.cards?.length ?? 0;
								const validHeading = Boolean(tab?.title || tab?.description);

								return (
									<Tabs.Panel key={tab.id || index} index={index}>
										<VStack gap="eds.dimension.48" alignItems="flex-start" w="full">
											{validHeading ? (
												<SectionHeading
													title={tab?.title}
													desc={tab?.description}
													className={sectionHeadingStyles}
													buttons={tab?.buttons}
													buttonAlignLeft={false}
												/>
											) : null}

											{featuredCardsLength ? (
												<TabbedBlogCardsMolecule
													cards={tab?.featuredCards}
													isFirstCardRow={featuredCardsLength === 1 || featuredCardsLength > 4}
													cardGridFitToWidth={true}
												/>
											) : null}
											{cardsLength ? (
												<TabbedBlogCardsMolecule
													cards={tab?.cards}
													isFirstCardRow={!!tab?.cardGridFitToWidth && cardsLength === 1}
													cardGridFitToWidth={!!tab?.cardGridFitToWidth}
												/>
											) : null}
										</VStack>
									</Tabs.Panel>
								);
							})}
						</VStack>
					</Tabs>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};
